import axios, { AxiosError } from "axios";
import { NIL } from "uuid";
import { getAuthToken } from "../../custom-hooks/useDecodedToken/useDecodedToken";
import { SaveResponse } from "../../models/api/save/save.response";
import { TnATimesheetConfirmationsDTO, TnATimesheetConfirmationSTO } from "../../models/tna-timesheet-confirmation";
import { BaseApiService } from "../base/base-api.service";

export interface SignatureFile {
  name: string;
  data: Blob;
  error?: string;
}

export const makeBlobUrl = (blob: Blob) => {
  const aFile = new Blob([blob], { type: blob?.type });
  // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
  const blobUrl = URL.createObjectURL(aFile);
  return blobUrl;
};

const getFileName = (contentDispositionHeader: string): string => {
  const fileNameUtf8Attr = "filename*=UTF-8''";
  const fileNameAttr = "filename=";
  let fileName = "";
  if (contentDispositionHeader.indexOf(fileNameUtf8Attr) !== -1) {
    fileName = contentDispositionHeader.split(fileNameUtf8Attr)[1].split(";")[0];
  } else if (contentDispositionHeader.indexOf(fileNameAttr) !== -1) {
    fileName = contentDispositionHeader.split(fileNameAttr)[1].split(";")[0];
  }

  return fileName;
};

export class TnATimesheetConfirmationsApiService extends BaseApiService<
  TnATimesheetConfirmationsDTO,
  TnATimesheetConfirmationSTO
> {
  constructor() {
    super({
      servicePath: "/TnATimesheetConfirmations",
      loadItemsPath: "/GetTnATimesheetConfirmations",
      saveItemsPath: "/SaveTnATimesheetConfirmations",
    });
  }

  /**
   * Create/Update/Delete items derived from STOModel
   * @param url
   * @param item
   * @param signature
   * @param timeout
   */
  public async saveItemWithFile(
    item: TnATimesheetConfirmationSTO,
    signature?: File,
    type?: string,
    timeout = this.defaultTimeout
  ): Promise<SaveResponse> {
    const request = this.createSaveRequest([item], timeout);
    const token = getAuthToken();
    const url = `${this.apiURL}${this.apiPaths.servicePath}${this.apiPaths.saveItemsPath}`;

    let response = { data: { isSucceeded: false } as SaveResponse };

    try {
      const formData = new FormData();
      formData.append("id", request.id);
      formData.append("invokerId", request.invokerId ?? NIL);
      formData.append("correlationId", request.correlationId ?? NIL);
      formData.append("timeout", "60");

      formData.append("isDeleted", false.toString());

      formData.append("currentCompanyAccountId", request.currentCompanyAccount?.id ?? NIL);
      formData.append("legalEntityId", item.legalEntity?.id ?? NIL);
      formData.append("employeeId", item.employee?.id ?? NIL);

      formData.append("itemId", item.id);

      formData.append("files[]", signature ?? new Blob(), signature?.name);
      formData.append("fileType", type ?? "");

      response = await axios.post<SaveResponse>(url, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      const error = err as AxiosError;
      let errorMessage = error?.response?.data?.error ? error?.response?.data?.error : error?.message ?? error;

      if (response.data) {
        response.data.error = errorMessage;
      }

      switch (error?.response?.status) {
        case 400: {
          errorMessage = "Bad request/wrong data sent.";
          break;
        }
        case 401: {
          errorMessage = "Not authenticated. Try to sign in again.";
          break;
        }
        case 403: {
          errorMessage = "Not authorized. Review your access rights.";
          break;
        }
        case 404: {
          errorMessage = "Data not found.";
          break;
        }

        case 415: {
          errorMessage = "Wrong media type.";
          break;
        }

        case 500: {
          errorMessage = "Failure saving file - Server error.";
          break;
        }

        case 502: {
          errorMessage = "Bad gateway / endpoint unreachable.";
          break;
        }

        case 503: {
          errorMessage = "Service unavailable.";
          break;
        }

        default:
          break;
      }
    }

    return response?.data;
  }
}
